import React, { useState, useEffect } from 'react';
import images from '../../images';
import { apiRequest } from '../../api';
import FilterModal from '../Modals/FilterModal';
import ReportModal from '../Modals/ReportModal';
import ApplyForMarriageModal from '../Modals/ApplyForMarriageModal';
import Pagination from '../Pagination';

const MyLeadsContent = ({ userID, packages, packageTypes, flowTypes }) => {
  const [showReportModal, setShowReportModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showApplyForMarriageModal, setShowApplyForMarriageModal] = useState(false);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [itemsLimit, setItemsLimit] = useState(20);
  const [appliedFilters, setAppliedFilters] = useState({
    createdAt: ['', ''],
    flow: '',
    label: '',
    region: '',
    marriageStatus: '',
    phone: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLeads, setSelectedLeads] = useState([]);

  // Метод для загрузки лидов с пагинацией и фильтрами
  const loadLeads = async () => {
    try {
      // Формируем параметры запроса
      const params = new URLSearchParams();
      params.append('limit', itemsLimit);
      params.append('offset', (currentPage - 1) * itemsLimit);
      // Если есть фильтр по дате, добавляем created_from и created_to в формате RFC3339 по МСК
      if (appliedFilters.createdAt && appliedFilters.createdAt.length === 2) {
        const [startDate, endDate] = appliedFilters.createdAt;
        if (startDate) {
          // Дата от: начало дня (00:00:00) по МСК (+03:00)
          // Формируем дату вручную с учетом часового пояса +03:00
          const fromDateString = `${startDate}T00:00:00Z`;
          params.append('created_from', fromDateString);
        }
        if (endDate) {
          // Дата по: конец дня (23:59:59.999) по МСК (+03:00)
          // Формируем дату вручную с учетом часового пояса +03:00
          const toDateString = `${endDate}T23:59:59Z`;
          params.append('created_to', toDateString);
        }
      }
      if (appliedFilters.flow) {
        params.append('flow', appliedFilters.flow);
      }

      if (appliedFilters.label) {
        params.append('utm', appliedFilters.label);
      }

      if (appliedFilters.region) {
        params.append('region', appliedFilters.region);
      }

      if (appliedFilters.phone) {
        params.append('phone', appliedFilters.phone);
      }
      // Загружаем лиды с примененными фильтрами
      const queryStr = params.toString();
      const loadedLeadsResponse = await apiRequest(`leads/${userID}/list?${queryStr}`, 'GET');
      const loadedLeads = loadedLeadsResponse.data.leads || [];
      setTotal(loadedLeadsResponse.data.total || 0);
      
      return loadedLeads;
    } catch (err) {
      console.error('Ошибка загрузки лидов:', err);
      throw err;
    }
  };
  console.log('appliedFilters:', appliedFilters);

  // Загрузка данных при монтировании компонента
  useEffect(() => {
    let isMounted = true;
    
    const loadData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        if (!isMounted) return;
        
        const loadedLeads = await loadLeads();
        
        if (!isMounted) return;
        
        if (isMounted) {
          setLeads(loadedLeads);
        }
      } catch (err) {
        if (isMounted) {
          setError('Не удалось загрузить данные');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
  }, [currentPage, appliedFilters, itemsLimit]);

  const handleGenerateReport = () => {
    setShowReportModal(true);
  };

  const handleOpenFilters = () => {
    setShowFilterModal(true);
  };

  const handleApplyForMarriage = () => {
    setShowApplyForMarriageModal(true);
  };

  const closeReportModal = () => {
    setShowReportModal(false);
  };

  const closeFilterModal = () => {
    setShowFilterModal(false);
  };

  const closeApplyForMarriageModal = () => {
    setShowApplyForMarriageModal(false);
    // Перезапрашиваем лиды с текущими фильтрами после закрытия модального окна
    loadLeads().then(loadedLeads => {
      setLeads(loadedLeads);
    });
  };

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters);
    setShowFilterModal(false);
    setCurrentPage(1); // Сброс на первую страницу при применении фильтров
    // setItemsLimit(20); // Сброс лимита на значение по умолчанию при применении фильтров
  };

  const handleBlurSearch = () => {
    if (searchTerm.trim() === '') {
      // Очищаем фильтр по телефону, если строка поиска пустая
      setAppliedFilters(prev => ({
        ...prev,
        phone: ''
      }));
    } else {
      setAppliedFilters(prev => ({
        ...prev,
        phone: searchTerm.trim()
      }));
      setCurrentPage(1);
    }
  };

  const handleResetFilters = () => {
    setAppliedFilters({
      createdAt: ['', ''],
      flow: '',
      label: '',
      region: '',
      marriageStatus: '',
      phone: ''
    });
    setSearchTerm('');
    setShowFilterModal(false);
    setCurrentPage(1); // Сброс на первую страницу при сбросе фильтров
    // setItemsLimit(20); // Сброс лимита на значение по умолчанию при сбросе фильтров
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => 
      prev.includes(leadId) 
        ? prev.filter(id => id !== leadId)
        : [...prev, leadId]
    );
  };

  const handleSelectAll = () => {
    // Выбираем только лиды без reject
    const selectableLeads = leads.filter(lead => !lead.reject);
    
    if (selectedLeads.length === selectableLeads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(selectableLeads.map(lead => lead.id));
    }
  };

  // const filteredLeads = leads.filter(lead =>
  //   lead.phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //   lead.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //   lead.region.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //   lead.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
  //   lead.flow.toLowerCase().includes(searchTerm.toLowerCase())
  // );
  // console.log(filteredLeads);
  // Показываем загрузку только для таблицы, но отображаем остальную часть страницы
  if (loading) {
    return (
      <main className="main-content">
        <div className="myleads-container">
          <div className="d-flex justify-content-between align-items-center mb-3 pb-2 myleads-header">
            <div className="d-flex align-items-center fs-5 fw-normal text-secondary myleads-title">
              <img
                  src={images.leads}
                  alt=""
                  className="header-icon"
              />
              Мои идентификации
            </div>
            <button
                className="btn btn-primary btn-report"
                onClick={handleGenerateReport}
            >
              <img
                  src={images.report}
                  alt=""
                  className="btn-icon"
              />
              Сформировать отчет
            </button>
          </div>

          <div className="d-flex align-items-center mb-3 gap-2">
            <div className="search-container">
              <img
                  src={images.search}
                  alt=""
                  className="search-icon"
              />
              <input
                  type="text"
                  placeholder="Поиск"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onBlur={handleBlurSearch}
                  className="form-control bg-white search-input"
                  style={{height: '100%'}}
              />
            </div>
            <button
                className="btn btn-secondary btn-filter"
                onClick={handleOpenFilters}
            >
              <img
                  src={images.filter}
                  alt=""
                  className="btn-icon"
              />
              Фильтры
            </button>
          </div>

          <div className="leads-table-container">
            <div className="loading">Загрузка идентификаций...</div>
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
        <main className="main-content">
          <div className="myleads-container">
            <div className="myleads-header">
              <div className="myleads-title">
                <img
                    src={images.leads}
                    alt=""
                    className="header-icon"
                />
                Мои идентификации
              </div>
            </div>
            <div className="error">{error}</div>
          </div>
        </main>
    );
  }

  return (
      <main className="main-content">
        <div className="myleads-container">
          <div className="d-flex justify-content-between align-items-center mb-3 pb-2 myleads-header">
            <div className="d-flex align-items-center fs-5 fw-normal text-secondary myleads-title">
              <img
                  src={images.leads}
                  alt=""
                  className="header-icon"
              />
              Мои идентификации
            </div>
            <button
                className="btn btn-primary btn-report"
                onClick={handleGenerateReport}
            >
              <img
                  src={images.report}
                  alt=""
                  className="btn-icon"
              />
              Сформировать отчет
            </button>
          </div>

          <div className="d-flex align-items-center mb-3 gap-2">
            <div className="search-container">
              <img
                  src={images.search}
                  alt=""
                  className="search-icon"
              />
              <input
                  type="text"
                  placeholder="Поиск"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onBlur={handleBlurSearch}
                  className="form-control bg-white search-input"
                  style={{height: '100%'}}
              />
            </div>
            <button
                className="btn btn-secondary btn-filter"
                onClick={handleOpenFilters}
            >
              <img
                  src={images.filter}
                  alt=""
                  className="btn-icon"
              />
              Фильтры
            </button>
          </div>

          <div className="table-responsive">
            <table className="table table-hover leads-table">
              <thead>
              <tr>
                <th>
                  <input
                      type="checkbox"
                      checked={selectedLeads.length === leads.filter(lead => !lead.reject).length && leads.length > 0}
                      onChange={handleSelectAll}
                  />
                </th>
                <th className="text-secondary">ID</th>
                <th className="text-secondary">UUID</th>
                <th className="text-secondary">Телефон</th>
                <th className="text-secondary">Город</th>
                <th className="text-secondary">Регион</th>
                <th className="text-secondary">Метка</th>
                <th className="text-secondary">Дата создания</th>
                <th className="text-secondary">Поток</th>
                <th className="text-secondary">Пакет</th>
                <th className="text-secondary">Брак</th>
              </tr>
              </thead>
              <tbody>
              {leads.map(lead => (
                  <tr key={lead.id}>
                    <td>
                      <input
                          type="checkbox"
                          checked={selectedLeads.includes(lead.id)}
                          onChange={() => handleSelectLead(lead.id)}
                          disabled={lead.reject}
                      />
                    </td>
                    <td>{lead.id}</td>
                    <td>{lead.soda_id}</td>
                    <td>{lead.phone}</td>
                    <td>{lead.city_lead}</td>
                    <td>{lead.region_lead}</td>
                    <td>{lead.utm_source}</td>
                    <td>{new Date(lead.created_at).toLocaleDateString('ru-RU')}</td>
                    <td>{packages.find(f => f.id === lead.paket)?.flow_guide_name || '-'}</td>
                    <td>{packages.find(f => f.id === lead.paket)?.paks_name || '-'}</td>
                    {/*<td>{lead.reject ? lead.reject_status ? 'Брак' : 'Заявка на брак' : ''}</td>*/}
                    <td>
                      {lead.reject_status ===  'wait'? 'Заявка на брак'
                          : lead.reject_status ===  'approve'? 'Брак'
                          :lead.reject_status === 'reject'? 'Отклонено'
                          : ''
                      }
                    </td>
                  </tr>
              ))}
              </tbody>
            </table>
          </div>

          {showReportModal && (
              <ReportModal
                  isOpen={showReportModal}
                  onClose={closeReportModal}
                  userID={userID}
                  packages={packages}
              />
          )}

          {showApplyForMarriageModal && (
              <ApplyForMarriageModal
                  isOpen={showApplyForMarriageModal}
                  onClose={closeApplyForMarriageModal}
                  leads={leads.filter(lead => selectedLeads.includes(lead.id))}
              />
          )}

          {showFilterModal && (
              <FilterModal
                  isOpen={showFilterModal}
                  onClose={closeFilterModal}
                  onApplyFilters={handleApplyFilters}
                  onResetFilters={handleResetFilters}
                  currentFilters={appliedFilters}
              />
          )}

          {/* Пагинация */}
          <Pagination 
            currentPage={currentPage}
            total={total}
            limit={itemsLimit}
            onPageChange={setCurrentPage}
            onLimitChange={setItemsLimit}
          />

          {/* Блок с выбранными лидами и кнопкой */}
          {selectedLeads.length > 0 && (
            <div className="myleads-footer">
              <span className="myleads-selected-count">Выбрано: {selectedLeads.length}</span>
              <button
                className="btn btn-primary-filled myleads-apply-button"
                onClick={handleApplyForMarriage}
              >
                Подать заявление на брак
              </button>
            </div>
          )}
        </div>
      </main>
  );
};

export default MyLeadsContent;
